import { isEmpty } from 'lodash';
import { useEffect, useMemo } from 'react';
import DataSourceHelper from '../../../../helper/DataSourceHelper';
import DependencyFlowHelper from '../../../../helper/DependencyFlowHelper';
import {
  useDSCActions,
  useDSCDataSourceRecordStore,
  useDSCGlobalStateStore,
} from '../../context/selectors';
import { useGetDataSourceRecord } from '../../hooks/useGetDataSourceRecord';
import { useIsDataSourceDisabled } from '../../hooks/useIsDataSourceDisabled';
import useShowDisabledDataSourceSnackbar from '../../hooks/useShowDisabledDataSourceSnackbar';

function useManualDataSourceTriggerIds() {
  const dsDependencyFlow = useDSCGlobalStateStore().hooks.dataSource.useDependencyFlow();
  const dsIntraDependencies = useDSCGlobalStateStore().hooks.dataSource.useIntraDependencies();
  const manuallyTriggeredDataSources = useDSCGlobalStateStore().hooks.useManuallyTriggeredDataSources();
  const dataSourceRecords = useDSCDataSourceRecordStore().hooks.useDataSources();
  const manualDataSourceTriggerIds = Object.keys(manuallyTriggeredDataSources);
  const { removeManualDataSourceStates, removeManuallyTriggeredDataSources } = useDSCActions();
  const { getDataSourceRecord } = useGetDataSourceRecord();
  const { showDisabledDSMessage } = useShowDisabledDataSourceSnackbar(getDataSourceRecord);
  const { getIsDisabled } = useIsDataSourceDisabled();

  const freeManualDataSourceTriggerIds = manualDataSourceTriggerIds.filter((key) => {
    const id = manuallyTriggeredDataSources[key].dataSourceId;
    return DependencyFlowHelper.areAllDependenciesFinished({
      dependencies: dsIntraDependencies?.[id],
      dependencyFlowInstance: dsDependencyFlow,
    });
  });

  const disabledKeys = useMemo(() => {
    const _ids = freeManualDataSourceTriggerIds.filter((key) => {
      const id = manuallyTriggeredDataSources[key].dataSourceId;
      const queryDisabled = DataSourceHelper.advancedOptions(dataSourceRecords[id]).queryDisabled;
      if (isEmpty(queryDisabled)) return false;

      return getIsDisabled({ disabled: queryDisabled });
    });

    return new Set(_ids);
  }, [
    dataSourceRecords,
    freeManualDataSourceTriggerIds,
    getIsDisabled,
    manuallyTriggeredDataSources,
  ]);

  const enabledKeys = freeManualDataSourceTriggerIds.filter((key) => {
    return !disabledKeys.has(key);
  });

  useEffect(() => {
    if (disabledKeys.size) {
      // as manual ds is fire and forget, we remove the entry because it is unnecessary as at the time of firing the disability was met
      disabledKeys.forEach((key) => {
        const dataSourceId = manuallyTriggeredDataSources[key].dataSourceId;
        manuallyTriggeredDataSources[key].callback?.();
        showDisabledDSMessage(dataSourceId);
      });
      removeManualDataSourceStates(Array.from(disabledKeys));
      removeManuallyTriggeredDataSources(Array.from(disabledKeys));
    }
  }, [
    disabledKeys,
    manuallyTriggeredDataSources,
    removeManualDataSourceStates,
    showDisabledDSMessage,
    removeManuallyTriggeredDataSources,
  ]);

  return {
    triggerIds: enabledKeys,
    requests: manuallyTriggeredDataSources,
  };
}

export default useManualDataSourceTriggerIds;
